import React, { useState, useEffect } from "react";
import greater from "../../images/greater.svg";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth-slice";
import { useGetTransactionsMutation } from "../../store/apis";

const TRANSACTION_TYPES = {
  ALL: "all",
  DEPOSIT: "deposit",
  WITHDRAWAL: "withdraw",
  BET: "bet",
  TAX: "tax",
};

const Transaction = () => {
  // State management
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedType, setSelectedType] = useState(TRANSACTION_TYPES.ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const [allTransactions, setAllTransactions] = useState([]); // Store all fetched transactions
  const [filteredTransactions, setFilteredTransactions] = useState([]); // Store filtered transactions
  const [paginatedTransactions, setPaginatedTransactions] = useState([]); // Store paginated transactions
  const itemsPerPage = 8;
  const user = useSelector(selectUser);

  // Use mutation to fetch transactions
  const [getTransactions, { isLoading }] = useGetTransactionsMutation();

  // Fetch all transactions only once (or when needed to refresh)
  const fetchAllTransactions = async () => {
    try {
      // Only pass the customer ID in the request
      const response = await getTransactions({
        customer_id: user.id,
        // Not passing any filters to get all data
      }).unwrap();

      if (response && response.rows) {
        // Map API response to component format
        const mappedData = response.rows.map((item) => ({
          referenceId: item.transaction_id.slice(0, 10),
          dateTime: item.created_date,
          description: getDescriptionFromType(
            item.transaction_type,
            item.reference_transaction_id
          ),
          amount:
            item.transaction_type === "deposit"
              ? `+${item.amount}`
              : `-${item.amount}`,
          status: item.status,
          balance: "N/A",
          type: item.transaction_type,
          // Store original date objects for filtering
          rawDate: new Date(item.created_date),
        }));

        setAllTransactions(mappedData);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    }
  };

  // Generate description based on transaction type
  function getDescriptionFromType(type, reference) {
    switch (type) {
      case "deposit":
        return `Deposit via Bank Transfer (Ref: ${reference})`;
      case "withdraw":
        return `Withdrawal Request (Ref: ${reference})`;
      case "bet":
        return `Sports Bet Placement (Ref: ${reference})`;
      case "tax":
        return `Excise Tax for bet id: ${reference}`;
      default:
        return `Transaction (Ref: ${reference})`;
    }
  }

  // Initial fetch of all transactions
  useEffect(() => {
    fetchAllTransactions();
  }, []);

  // Apply filters and update filtered transactions
  useEffect(() => {
    if (allTransactions.length === 0) return;

    let filtered = [...allTransactions];

    // Apply transaction type filter if not "all"
    if (selectedType !== TRANSACTION_TYPES.ALL) {
      filtered = filtered.filter((item) => item.type === selectedType);
    }

    // Apply date from filter if provided
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((item) => item.rawDate >= fromDate);
    }

    // Apply date to filter if provided
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((item) => item.rawDate <= toDate);
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [allTransactions, selectedType, dateFrom, dateTo]);

  // Apply pagination to filtered transactions
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedTransactions(filteredTransactions.slice(startIndex, endIndex));
  }, [filteredTransactions, currentPage]);

  // Format amount with color based on transaction type
  const getAmountDisplay = (amount) => {
    const isNegative = amount.startsWith("-");
    return (
      <span
        className={`font-medium ${
          isNegative ? "text-red-600" : "text-green-600"
        } dark:text-white`}
      >
        {amount}
      </span>
    );
  };

  // Format date for display
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";

    const date = new Date(dateTime);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  // Handle page change
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate pagination numbers
  const getPaginationNumbers = () => {
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    let startPage = Math.max(currentPage - 1, 2);
    let endPage = Math.min(currentPage + 1, totalPages - 1);

    if (currentPage <= 2) {
      endPage = 4;
    }
    if (currentPage >= totalPages - 1) {
      startPage = totalPages - 3;
    }

    if (startPage > 2) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="p-[10px_15px_40px] md:p-[10px_20px_40px] s16:p-[15px_30px_60px] s18:p-[20px_30px_100px]">
      <div className="overflow-hidden shadow-dashboardshadow w-full max-w-[1780px] mx-auto bg-doubleextralightwhite rounded-[18px] md:rounded-[25px] border border-solid border-lightblueborder dark:border-[#0C3D8C] dark:bg-bluedarkmode">
        <div className="overflow-auto">
          <div className="min-w-[900px] flex flex-wrap items-center justify-between p-[20px_20px] md:p-[10px_37px] s16:p-[16px_37px] s18:p-[20px_37px] bg-lightbluebg rounded-[18px_18px_0px_0px] md:rounded-[25px_25px_0px_0px] border border-solid border-b-extrathinblack dark:bg-bluedarkmode dark:border-[#667487]">
            <div className="flex items-center gap-[30px] xl:gap-[75px]">
              <div className="flex items-center gap-[13px]">
                <label className="text-sm xl:text-base s15:text-[20px] s18:text-[22px] text-mutedblue font-bold !leading-tight s18:!leading-[160%] dark:text-white">
                  From:
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="text-white text-sm s16:text-lg s18:text-[22px] font-bold !leading-none s18:!leading-[160%] shadow-dashboardshadow border border-solid bg-darktitleColor rounded-[6px] min-w-[180px] xl:min-w-[227px] py-2 px-2 md:px-3 s18:px-4 md:py-2 s18:py-4 w-full"
                />
              </div>
              <div className="flex items-center gap-[13px]">
                <label className="text-sm xl:text-base s15:text-[20px] s18:text-[22px] text-mutedblue font-bold !leading-tight s18:!leading-[160%] dark:text-white">
                  To:
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="text-white text-sm s16:text-lg s18:text-[22px] font-bold !leading-none s18:!leading-[160%] shadow-dashboardshadow border border-solid bg-darktitleColor min-w-[180px] xl:min-w-[227px] rounded-[6px] py-2 px-2 md:px-3 s18:px-4 md:py-2 s18:py-4 w-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-[13px]">
              <label className="text-sm xl:text-base s15:text-[20px] s18:text-[22px] text-white font-bold leading-[160%] dark:text-white">
                Type:
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="select text-white text-sm s16:text-lg s18:text-[22px] py-2 px-2 font-bold !leading-none s18:!leading-[160%] shadow-dashboardshadow border border-solid bg-darktitleColor min-w-[190px] rounded-[6px] md:px-3 s18:px-4 md:py-2 s18:py-4 w-full appearance-none outline-none"
              >
                <option value={TRANSACTION_TYPES.ALL}>All</option>
                <option value={TRANSACTION_TYPES.DEPOSIT}>Deposits</option>
                <option value={TRANSACTION_TYPES.WITHDRAWAL}>
                  Withdrawals
                </option>
                {/* <option value={TRANSACTION_TYPES.BET}>Bets</option>
                <option value={TRANSACTION_TYPES.TAX}>Tax</option> */}
              </select>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-auto p-[15px] md:p-[10px_25px] xl:p-[12px_35px] s18:p-[15px_35px]">
          {isLoading ? (
            <div className="text-center py-6 text-yellow font-medium">
              Loading transactions...
            </div>
          ) : paginatedTransactions.length > 0 ? (
            <table className="table-auto w-full min-w-[900px] text-sm text-left">
              <thead>
                <tr>
                  <th className="px-[15px] py-[10px] s15:py-3 s18:px-[23px] text-black text-sm s15:text-base s18:text-[22px] font-bold !leading-snug s18:leading-[159%] dark:text-white">
                    Reference ID
                  </th>
                  <th className="px-[15px] py-[10px] s15:py-3 s18:px-[23px] text-black text-sm s15:text-base s18:text-[22px] font-bold !leading-snug s18:leading-[159%] dark:text-white">
                    Date/Time
                  </th>
                  {/* <th className="px-[15px] py-[10px] s15:py-3 s18:px-[23px] text-black text-sm s15:text-base s18:text-[22px] font-bold !leading-snug s18:leading-[159%] dark:text-white">
                    Description
                  </th> */}
                  <th className="px-[15px] py-[10px] s15:py-3 s18:px-[23px] text-black text-sm s15:text-base s18:text-[22px] font-bold !leading-snug s18:leading-[159%] dark:text-white">
                    Amount
                  </th>
                  <th className="px-[15px] py-[10px] s15:py-3 s18:px-[23px] text-black text-sm s15:text-base s18:text-[22px] font-bold !leading-snug s18:leading-[159%] dark:text-white">
                    Status
                  </th>
                  <th className="px-[15px] py-[10px] s15:py-3 s18:px-[23px] text-black text-sm s15:text-base s18:text-[22px] font-bold !leading-snug s18:leading-[159%] dark:text-white">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((item, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 0
                        ? "bg-tableblue dark:bg-darktitleColor"
                        : "bg-transparent"
                    } text-[#0C3D8C] dark:text-white text-sm s15:text-base s18:text-2xl font-light s18:leading-[141%] !leading-tight rounded-[12px] font-poppins`}
                  >
                    <td className="py-[10px] s18:py-[25px] px-[15px] s18:px-[23px] font-medium rounded-[12px_0px_0px_12px]">
                      {item.referenceId}
                    </td>
                    <td className="py-[10px] s18:py-[25px] px-[15px] s18:px-[23px]">
                      {formatDateTime(item.dateTime)}
                    </td>
                    {/* <td className="py-[10px] s18:py-[25px] px-[15px] s18:px-[23px]">
                      {item.description}
                    </td> */}
                    <td className="py-[10px] s18:py-[25px] px-[15px] s18:px-[23px]">
                      {getAmountDisplay(item.amount)}
                    </td>
                    <td className="py-[10px] s18:py-[25px] px-[15px] s18:px-[23px]">
                      {item.status}
                    </td>
                    <td className="py-[10px] s18:py-[25px] px-[15px] s18:px-[23px] rounded-[0px_12px_12px_0px] capitalize">
                      {item.type}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-6 text-mutedblue font-medium">
              No transactions found.
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {filteredTransactions.length > 0 && (
        <div className="mt-5">
          <div className="flex gap-2 xl:gap-[13px] justify-center items-center">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="disabled:opacity-50"
            >
              <img
                src={greater}
                alt="previous"
                className="object-contain cursor-pointer rotate-180 dark:brightness-[100] dark:hover:brightness-50"
              />
            </button>

            {getPaginationNumbers().map((page, index) => (
              <div
                key={index}
                onClick={() =>
                  typeof page === "number" && handlePageChange(page)
                }
                className={`rounded-[4px] cursor-pointer size-5 xl:size-8 text-xs xl:text-sm font-semibold 
                  ${
                    typeof page === "number"
                      ? currentPage === page
                        ? "bg-bluedarkmode text-white"
                        : "dark:bg-darktitleColor dark:hover:bg-white bg-darkblue/10 dark:hover:text-darkblue dark:text-white hover:text-white hover:bg-darkblue"
                      : "text-[#555752] dark:text-white"
                  } 
                  transition-all duration-300 flex justify-center items-center`}
              >
                {page}
              </div>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={
                currentPage ===
                Math.ceil(filteredTransactions.length / itemsPerPage)
              }
              className="disabled:opacity-50"
            >
              <img
                src={greater}
                alt="next"
                className="object-contain cursor-pointer dark:brightness-[100] dark:hover:brightness-50"
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transaction;
